import React, { useState } from "react";
import { Button } from "./ui";
import { useNavigate } from "react-router-dom";
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody, MDBDropdown, MDBDropdownToggle, MDBDropdownMenu, MDBDropdownItem, MDBSpinner } from "mdb-react-ui-kit";
import { api } from "../services";
import CustomDropdown from "./dropdown/CustomDropdown";
import { useTranslation } from "react-i18next";
import NativeInput from "./NativeInput";

const PAPER_SIZES = [
  { label: "A4 (210 x 297 mm)", value: "A4", mm: "210x297" },
  { label: "A5 (148 x 210 mm)", value: "A5", mm: "148x210" },
  { label: "A6 (105 x 148 mm)", value: "A6", mm: "105x148" },
];
const TIMEZONES = [
  { label: "UTC+03:00 (KSA)", value: "+03:00" },
  { label: "UTC+04:00 (UAE)", value: "+04:00" },
  { label: "UTC+02:00 (Egypt)", value: "+02:00" },
  { label: "UTC+03:00 (Qatar/Bahrain/Kuwait)", value: "+03:00" },
  { label: "UTC+03:00 (Iraq)", value: "+03:00" },
  { label: "UTC+03:00 (Yemen)", value: "+03:00" },
  { label: "UTC+03:00 (Syria)", value: "+03:00" },
  { label: "UTC+03:00 (Jordan)", value: "+03:00" },
  { label: "UTC+03:00 (Palestine)", value: "+03:00" },
  { label: "UTC+03:00 (Lebanon)", value: "+03:00" },
  { label: "UTC+03:30 (Iran)", value: "+03:30" },
  { label: "UTC+04:30 (Afghanistan)", value: "+04:30" },
  { label: "UTC+01:00 (Morocco/Algeria/Tunisia)", value: "+01:00" },
  { label: "UTC+00:00 (GMT)", value: "+00:00" },
];
const BUSINESS_TIMEZONES = [
  { label: "KSA (Asia/Riyadh)", value: "Asia/Riyadh" },
  { label: "UAE (Asia/Dubai)", value: "Asia/Dubai" },
  { label: "Qatar (Asia/Qatar)", value: "Asia/Qatar" },
  { label: "Bahrain (Asia/Bahrain)", value: "Asia/Bahrain" },
  { label: "Kuwait (Asia/Kuwait)", value: "Asia/Kuwait" },
  { label: "Oman (Asia/Muscat)", value: "Asia/Muscat" },
  { label: "Egypt (Africa/Cairo)", value: "Africa/Cairo" },
  { label: "Jordan (Asia/Amman)", value: "Asia/Amman" },
  { label: "Lebanon (Asia/Beirut)", value: "Asia/Beirut" },
  { label: "Syria (Asia/Damascus)", value: "Asia/Damascus" },
  { label: "Iraq (Asia/Baghdad)", value: "Asia/Baghdad" },
  { label: "Palestine (Asia/Gaza)", value: "Asia/Gaza" },
  { label: "Iran (Asia/Tehran)", value: "Asia/Tehran" },
  { label: "Morocco (Africa/Casablanca)", value: "Africa/Casablanca" },
  { label: "Algeria (Africa/Algiers)", value: "Africa/Algiers" },
  { label: "Tunisia (Africa/Tunis)", value: "Africa/Tunis" },
];

const initialForm = {
  storeName: "",
  branchName: "",
  currency: "SAR",
  printerType: "thermal",
  paperSize: "A6",
  billingType: "Billing",
  dateTimeLocal: "+03:00",
  logo: "",
  business_tax_number: "",
  business_timezone: "Asia/Riyadh",
  receipt_footer: "",
  receipt_header: "",
  receipt_invoice_title: "",
  receipt_localized_language: "ar",
  receipt_main_language: "en",
  tax_registration_name: "",
  receipt_hide_free_modifier_options: false,
  receipt_show_calories: false,
  receipt_show_check_number: true,
  receipt_show_closer_username: false,
  receipt_show_creator_username: true,
  receipt_show_order_number: true,
  receipt_show_rounding: true,
  receipt_show_subtotal: false,
  numberOfCopies: 1,
};

export default function PrinterAddPage() {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const [form, setForm] = useState({
    ...initialForm,
    storeId: "",
    branchId: "",
  });
  const [stores, setStores] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  React.useEffect(() => {
    fetchStores();
  }, []);

  React.useEffect(() => {
    if (selectedStore) {
      fetchBranches(selectedStore);
    } else {
      setBranches([]);
      setSelectedBranch("");
      setForm((prev) => ({ ...prev, branchId: "" }));
    }
  }, [selectedStore]);

  React.useEffect(() => {
    if (stores && stores.length > 0) {
      const updatedStores = stores.map((store) => ({
        ...store,
        displayName: isArabic
          ? store?.name?.ar || store?.name_ar || store?.name?.en || store?.name
          : store?.name?.en || store?.name,
      }));
      setStores(updatedStores);
    }
    if (branches && branches.length > 0) {
      const updatedBranches = branches.map((branch) => ({
        ...branch,
        displayName: isArabic
          ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name
          : branch?.name?.en || branch?.name,
      }));
      setBranches(updatedBranches);
    }
  }, [isArabic]);

  const PRINTER_TYPES = [
    { label: `${t("Thermal")}`, value: "thermal" },
    { label: `${t("Laser")}`, value: "laser" },
    { label: `${t("DotMetrix")}`, value: "dotmetrix" },
  ];
  const fetchStores = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await api.get("/stores/my", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rawStores = res.data.stores || [];
      const localizedStores = rawStores.map((store) => ({
        ...store,
        displayName: isArabic
          ? store?.name?.ar || store?.name_ar || store?.name?.en || store?.name
          : store?.name?.en || store?.name,
      }));
      setStores(localizedStores);
    } catch (err) {
      setStores([]);
    }
  };

  const fetchBranches = async (storeId) => {
    setIsLoadingBranches(true);
    try {
      const token = localStorage.getItem("token");
      const res = await api.get(`/stores/${storeId}/branches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rawBranches = res.data.branches || [];
      const localizedBranches = rawBranches.map((branch) => ({
        ...branch,
        displayName: isArabic
          ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name
          : branch?.name?.en || branch?.name,
      }));
      setBranches(localizedBranches);
    } catch (err) {
      setBranches([]);
    } finally {
      setIsLoadingBranches(false);
    }
  };

  const handleStoreSelect = (storeId) => {
    setSelectedStore(storeId);
    setForm((prev) => ({ ...prev, storeId, branchId: "" }));
    setSelectedBranch("");
  };

  const handleBranchSelect = (branchId) => {
    setSelectedBranch(branchId);
    setForm((prev) => ({ ...prev, branchId }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDropdownChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "paperSize"
        ? { paperSizeMM: PAPER_SIZES.find((p) => p.value === value)?.mm || "" }
        : {}),
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.storeId) newErrors.storeId = t("Store is required");
    if (!form.branchId) newErrors.branchId = t("Branch is required");
    [
      "currency",
      "printerType",
      "paperSize",
      "billingType",
      "dateTimeLocal",
      "logo",
      "business_tax_number",
      "business_timezone",
      "receipt_footer",
      "receipt_header",
      "receipt_invoice_title",
      "receipt_localized_language",
      "receipt_main_language",
      "tax_registration_name",
    ].forEach((field) => {
      if (
        !form[field] ||
        (typeof form[field] === "string" && form[field].trim() === "")
      ) {
        newErrors[field] = t("Required");
      }
    });
    [
      "receipt_hide_free_modifier_options",
      "receipt_show_calories",
      "receipt_show_check_number",
      "receipt_show_closer_username",
      "receipt_show_creator_username",
      "receipt_show_order_number",
      "receipt_show_rounding",
      "receipt_show_subtotal",
    ].forEach((field) => {
      if (typeof form[field] !== "boolean") {
        newErrors[field] = "Required";
      }
    });
    if (
      !form.numberOfCopies ||
      form.numberOfCopies < 1 ||
      form.numberOfCopies > 5
    )
      newErrors.numberOfCopies = t("Must be 1-5");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const token = localStorage.getItem("token");
    try {
      await api.post("/printer-settings", form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage(t("Created successfully"));
      setTimeout(() => navigate("/printer-settings"), 1000);
    } catch (err) {
      setMessage(t("Error saving settings"));
    }
  };

  return (
    <div className="printer-add-page-root oh-page">
      <MDBContainer fluid className="content-area flex-grow-1">
        <div className="oh-form-page">
          <h1 className="page-title-header">{t("addprinterconfiguration")}</h1>
          <div className="card oh-form-card shadow-sm">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                {/* Store Dropdown */}
                <div className="mb-3 form-group">
                  <label className="form-label fw-semibold">
                    {t("store")} <span className="text-danger">*</span>
                  </label>
                  <MDBDropdown className="w-100">
                    <MDBDropdownToggle
                      color="light"
                      className={`w-100 text-start ${
                        errors.storeId ? "is-invalid" : ""
                      }`}
                    >
                      {selectedStore
                        ? stores.find((s) => s._id === selectedStore)?.displayName || stores.find((s) => s._id === selectedStore)?.name
                        : `${t("Select store")}`}
                    </MDBDropdownToggle>
                    <MDBDropdownMenu className="w-100">
                      {stores.map((store) => (
                        <MDBDropdownItem
                          key={store._id}
                          onClick={() => handleStoreSelect(store._id)}
                        >
                          {store.displayName || store.name}
                        </MDBDropdownItem>
                      ))}
                    </MDBDropdownMenu>
                  </MDBDropdown>
                  {errors.storeId && (
                    <div className="text-danger small mb-2">
                      {errors.storeId}
                    </div>
                  )}
                </div>
                {/* Branch Dropdown */}
                <div className="mb-3 form-group">
                 
                  <CustomDropdown
                    options={branches}
                    handleSelect={handleBranchSelect}
                    selectedOption={selectedBranch}
                    placeholder={
                      isLoadingBranches
                        ? "Loading branches..."
                        : `${t("Select branch")}`
                    }
                    backgroundColor="#fff"
                  />

                  {errors.branchId && (
                    <div className="text-danger small mb-2">
                      {errors.branchId}
                    </div>
                  )}
                </div>
                {/* Currency, Printer Type, ... rest of form ... */}
                <div className="mb-3 form-group">
                 
                  <NativeInput
                    label={t("currency")}
                    name="currency"
                    value={form.currency}
                    disabled
                    className="mb-2"
                  />
                  {errors.currency && (
                    <div className="text-danger small mb-2">
                      {errors.currency}
                    </div>
                  )}
                </div>
                {/* Printer Type Dropdown */}
                <div className="mb-3 form-group">
                  <label className="form-label fw-semibold">
                    {t("printertype")} <span className="text-danger">*</span>
                  </label>
                  <MDBDropdown className="w-100">
                    <MDBDropdownToggle
                      color="light"
                      className="w-100 text-start"
                    >
                      {PRINTER_TYPES.find((p) => p.value === form.printerType)
                        ?.label || "Select Printer Type"}
                    </MDBDropdownToggle>
                    <MDBDropdownMenu className="w-100">
                      {PRINTER_TYPES.map((type) => (
                        <MDBDropdownItem
                          key={type.value}
                          onClick={() =>
                            handleDropdownChange("printerType", type.value)
                          }
                        >
                          {type.label}
                        </MDBDropdownItem>
                      ))}
                    </MDBDropdownMenu>
                  </MDBDropdown>
                  {errors.printerType && (
                    <div className="text-danger small mb-2">
                      {errors.printerType}
                    </div>
                  )}
                </div>
                {/* Paper Size Dropdown */}
                <div className="mb-3 form-group">
                  <label className="form-label fw-semibold">
                    {t("papersize")} <span className="text-danger">*</span>
                  </label>
                  <MDBDropdown className="w-100">
                    <MDBDropdownToggle
                      color="light"
                      className="w-100 text-start"
                    >
                      {PAPER_SIZES.find((p) => p.value === form.paperSize)
                        ?.label || "Select Paper Size"}
                    </MDBDropdownToggle>
                    <MDBDropdownMenu className="w-100">
                      {PAPER_SIZES.map((size) => (
                        <MDBDropdownItem
                          key={size.value}
                          onClick={() =>
                            handleDropdownChange("paperSize", size.value)
                          }
                        >
                          {size.label}
                        </MDBDropdownItem>
                      ))}
                    </MDBDropdownMenu>
                  </MDBDropdown>
                  {errors.paperSize && (
                    <div className="text-danger small mb-2">
                      {errors.paperSize}
                    </div>
                  )}
                </div>
                {/* Number of Copies */}
                <div className="mb-3 form-group">
                  <label className="form-label fw-semibold">
                    {t("numberofcopies")} <span className="text-danger">*</span>
                  </label>
                  <NativeInput
                    type="number"
                    name="numberOfCopies"
                    min={1}
                    max={5}
                    value={form.numberOfCopies || 1}
                    onChange={(e) => {
                      let val = parseInt(e.target.value, 10);
                      if (isNaN(val)) val = 1;
                      if (val < 1) val = 1;
                      if (val > 5) val = 5;
                      setForm((prev) => ({ ...prev, numberOfCopies: val }));
                    }}
                    className="mb-2"
                    required
                    invalid={!!errors.numberOfCopies}
                  />
                  {errors.numberOfCopies && (
                    <div className="text-danger small mb-2">
                      {errors.numberOfCopies}
                    </div>
                  )}
                </div>
                {/* Billing Type Dropdown */}
                <div className="mb-3 form-group">
                  <label className="form-label fw-semibold">
                    {t("billingtype")} <span className="text-danger">*</span>
                  </label>
                  <MDBDropdown className="w-100">
                    <MDBDropdownToggle
                      color="light"
                      className="w-100 text-start"
                    >
                      {form.billingType}
                    </MDBDropdownToggle>
                    <MDBDropdownMenu className="w-100">
                      <MDBDropdownItem
                        onClick={() =>
                          handleDropdownChange("billingType", "Billing")
                        }
                      >
                        {t("billing")}
                      </MDBDropdownItem>
                      <MDBDropdownItem
                        onClick={() =>
                          handleDropdownChange("billingType", "Kitchen")
                        }
                      >
                        {t("Kitchen")}
                      </MDBDropdownItem>
                    </MDBDropdownMenu>
                  </MDBDropdown>
                  {errors.billingType && (
                    <div className="text-danger small mb-2">
                      {errors.billingType}
                    </div>
                  )}
                </div>
                {/* Date Time Local Dropdown */}
                <div className="mb-3 form-group">
                  <label className="form-label fw-semibold">
                    {t("datetimelocal")} <span className="text-danger">*</span>
                  </label>
                  <MDBDropdown className="w-100">
                    <MDBDropdownToggle
                      color="light"
                      className="w-100 text-start"
                    >
                      {TIMEZONES.find((tz) => tz.value === form.dateTimeLocal)
                        ?.label || "Select Timezone"}
                    </MDBDropdownToggle>
                    <MDBDropdownMenu className="w-100">
                      {TIMEZONES.map((tz) => (
                        <MDBDropdownItem
                          key={tz.value}
                          onClick={() =>
                            handleDropdownChange("dateTimeLocal", tz.value)
                          }
                        >
                          {tz.label}
                        </MDBDropdownItem>
                      ))}
                    </MDBDropdownMenu>
                  </MDBDropdown>
                  {errors.dateTimeLocal && (
                    <div className="text-danger small mb-2">
                      {errors.dateTimeLocal}
                    </div>
                  )}
                </div>
                <div className="mb-3 form-group">
                 
                  <NativeInput
                    label={t("logourl")}
                    name="logo"
                    value={form.logo}
                    onChange={handleChange}
                    className="mb-2"
                    required
                    invalid={!!errors.logo}
                  />
                  {errors.logo && (
                    <div className="text-danger small mb-2">{errors.logo}</div>
                  )}
                </div>
                {/* Business Timezone Dropdown */}
                <div className="mb-3 form-group">
                  <label className="form-label fw-semibold">
                    {t("bussinesstimezone")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <MDBDropdown className="w-100">
                    <MDBDropdownToggle
                      color="light"
                      className="w-100 text-start"
                    >
                      {BUSINESS_TIMEZONES.find(
                        (tz) => tz.value === form.business_timezone
                      )?.label || "Select Business Timezone"}
                    </MDBDropdownToggle>
                    <MDBDropdownMenu className="w-100">
                      {BUSINESS_TIMEZONES.map((tz) => (
                        <MDBDropdownItem
                          key={tz.value}
                          onClick={() =>
                            handleDropdownChange("business_timezone", tz.value)
                          }
                        >
                          {tz.label}
                        </MDBDropdownItem>
                      ))}
                    </MDBDropdownMenu>
                  </MDBDropdown>
                  {errors.business_timezone && (
                    <div className="text-danger small mb-2">
                      {errors.business_timezone}
                    </div>
                  )}
                </div>
                <div className="mb-3 form-group">
                 
                  <NativeInput
                    label={t("businesstaxnumber")}
                    name="business_tax_number"
                    value={form.business_tax_number}
                    onChange={handleChange}
                    className="mb-2"
                    required
                    invalid={!!errors.business_tax_number}
                  />
                  {errors.business_tax_number && (
                    <div className="text-danger small mb-2">
                      {errors.business_tax_number}
                    </div>
                  )}
                </div>
                <div className="mb-3 form-group">
                 
                  <NativeInput
                    label={t("recieptfooter")}
                    name="receipt_footer"
                    value={form.receipt_footer}
                    onChange={handleChange}
                    className="mb-2"
                    required
                    invalid={!!errors.receipt_footer}
                  />
                  {errors.receipt_footer && (
                    <div className="text-danger small mb-2">
                      {errors.receipt_footer}
                    </div>
                  )}
                </div>
                <div className="mb-3 form-group">
                  
                  <NativeInput
                    label={t("recieptheader")}
                    name="receipt_header"
                    value={form.receipt_header}
                    onChange={handleChange}
                    className="mb-2"
                    required
                    invalid={!!errors.receipt_header}
                  />
                  {errors.receipt_header && (
                    <div className="text-danger small mb-2">
                      {errors.receipt_header}
                    </div>
                  )}
                </div>
                <div className="mb-3 form-group">
                 
                  <NativeInput
                    label={t("recieptinvoicetitle")}
                    name="receipt_invoice_title"
                    value={form.receipt_invoice_title}
                    onChange={handleChange}
                    className="mb-2"
                    required
                    invalid={!!errors.receipt_invoice_title}
                  />
                  {errors.receipt_invoice_title && (
                    <div className="text-danger small mb-2">
                      {errors.receipt_invoice_title}
                    </div>
                  )}
                </div>
                <div className="mb-3 form-group">
                 
                  <NativeInput
                    label={t("recieptlocalizedlanguage")}
                    name="receipt_localized_language"
                    value={form.receipt_localized_language}
                    onChange={handleChange}
                    className="mb-2"
                    required
                    invalid={!!errors.receipt_localized_language}
                  />
                  {errors.receipt_localized_language && (
                    <div className="text-danger small mb-2">
                      {errors.receipt_localized_language}
                    </div>
                  )}
                </div>
                <div className="mb-3 form-group">
                  
                  <NativeInput
                    label={t("receiptmainlanguage")}
                    name="receipt_main_language"
                    value={form.receipt_main_language}
                    onChange={handleChange}
                    className="mb-2"
                    required
                    invalid={!!errors.receipt_main_language}
                  />
                  {errors.receipt_main_language && (
                    <div className="text-danger small mb-2">
                      {errors.receipt_main_language}
                    </div>
                  )}
                </div>
                <div className="mb-3 form-group">
                 
                  <NativeInput
                    label={t("Tax Registration Name")}
                    name="tax_registration_name"
                    value={form.tax_registration_name}
                    onChange={handleChange}
                    className="mb-2"
                    required
                    invalid={!!errors.tax_registration_name}
                  />
                  {errors.tax_registration_name && (
                    <div className="text-danger small mb-2">
                      {errors.tax_registration_name}
                    </div>
                  )}
                </div>
                {/* Boolean fields as checkboxes */}
                <h5 className="mt-4 mb-3">{t("recieptoptions")}</h5>
                <div className="form-check d-flex align-items-center mb-2">
                  <input
                    type="checkbox"
                    name="receipt_hide_free_modifier_options"
                    checked={!!form.receipt_hide_free_modifier_options}
                    onChange={handleChange}
                    className="form-check-input me-2"
                  />
                  <label className="form-check-label">
                    {t("hidefreemodifieroptions")}
                  </label>
                  {errors.receipt_hide_free_modifier_options && (
                    <div className="text-danger small">Required</div>
                  )}
                </div>
                <div className="form-check d-flex align-items-center mb-2">
                  <input
                    type="checkbox"
                    name="receipt_show_calories"
                    checked={!!form.receipt_show_calories}
                    onChange={handleChange}
                    className="form-check-input me-2"
                  />
                  <label className="form-check-label">
                    {t("showcalories")}
                  </label>
                  {errors.receipt_show_calories && (
                    <div className="text-danger small">Required</div>
                  )}
                </div>
                <div className="form-check d-flex align-items-center mb-2">
                  <input
                    type="checkbox"
                    name="receipt_show_check_number"
                    checked={!!form.receipt_show_check_number}
                    onChange={handleChange}
                    className="form-check-input me-2"
                  />
                  <label className="form-check-label">
                    {t("showchecknumber")}
                  </label>
                  {errors.receipt_show_check_number && (
                    <div className="text-danger small">Required</div>
                  )}
                </div>
                <div className="form-check d-flex align-items-center mb-2">
                  <input
                    type="checkbox"
                    name="receipt_show_closer_username"
                    checked={!!form.receipt_show_closer_username}
                    onChange={handleChange}
                    className="form-check-input me-2"
                  />
                  <label className="form-check-label">
                    {t("showcloserusername")}
                  </label>
                  {errors.receipt_show_closer_username && (
                    <div className="text-danger small">Required</div>
                  )}
                </div>
                <div className="form-check d-flex align-items-center mb-2">
                  <input
                    type="checkbox"
                    name="receipt_show_creator_username"
                    checked={!!form.receipt_show_creator_username}
                    onChange={handleChange}
                    className="form-check-input me-2"
                  />
                  <label className="form-check-label">
                    {t("showcreatorusername")}
                  </label>
                  {errors.receipt_show_creator_username && (
                    <div className="text-danger small">Required</div>
                  )}
                </div>
                <div className="form-check d-flex align-items-center mb-2">
                  <input
                    type="checkbox"
                    name="receipt_show_order_number"
                    checked={!!form.receipt_show_order_number}
                    onChange={handleChange}
                    className="form-check-input me-2"
                  />
                  <label className="form-check-label">
                    {t("showordernumber")}
                  </label>
                  {errors.receipt_show_order_number && (
                    <div className="text-danger small">Required</div>
                  )}
                </div>
                <div className="form-check d-flex align-items-center mb-2">
                  <input
                    type="checkbox"
                    name="receipt_show_rounding"
                    checked={!!form.receipt_show_rounding}
                    onChange={handleChange}
                    className="form-check-input me-2"
                  />
                  <label className="form-check-label">
                    {t("showrounding")}
                  </label>
                  {errors.receipt_show_rounding && (
                    <div className="text-danger small">Required</div>
                  )}
                </div>
                <div className="form-check d-flex align-items-center mb-2">
                  <input
                    type="checkbox"
                    name="receipt_show_subtotal"
                    checked={!!form.receipt_show_subtotal}
                    onChange={handleChange}
                    className="form-check-input me-2"
                  />
                  <label className="form-check-label">
                    {t("showsubtotal")}
                  </label>
                  {errors.receipt_show_subtotal && (
                    <div className="text-danger small">Required</div>
                  )}
                </div>
                <Button type="submit" color="primary" className="me-2">
                  {t("save")}
                </Button>
                <Button
                  color="secondary"
                  onClick={() => navigate("/printer-settings")}
                >
                  {t("cancel")}
                </Button>
                {message && (
                  <div className="mt-2 alert alert-info">{message}</div>
                )}
              </form>
            </div>
          </div>
        </div>
      </MDBContainer>
    </div>
  );
}
